import { ReactNode, useEffect, useState } from 'react';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';
import { supabase } from '@/integrations/supabase/client';
import MaintenanceMode from './MaintenanceMode';

interface MaintenanceWrapperProps {
  children: ReactNode;
}

const MaintenanceWrapper = ({ children }: MaintenanceWrapperProps) => {
  const { settings, loading } = usePlatformSettings();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);

  useEffect(() => {
    // Only run the admin check when maintenance mode is actually active —
    // otherwise we waste a Supabase RPC on every page load for every visitor.
    if (!settings.maintenance_mode) {
      setAdminCheckLoading(false);
      return;
    }

    let cancelled = false;
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) {
            setIsAdmin(false);
            setAdminCheckLoading(false);
          }
          return;
        }

        // Check for admin roles using the secure has_any_role RPC
        const { data: hasAdminRole } = await supabase.rpc('has_any_role', {
          _user_id: user.id,
          _roles: ['super_admin', 'finance_admin', 'support_admin', 'moderator']
        });

        if (!cancelled) setIsAdmin(!!hasAdminRole);
      } catch (error) {
        console.error('Error checking admin status:', error);
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setAdminCheckLoading(false);
      }
    };

    checkAdminStatus();
    return () => { cancelled = true; };
  }, [settings.maintenance_mode]);

  if (settings.maintenance_mode && (loading || adminCheckLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Allow admin users to bypass maintenance mode
  if (settings.maintenance_mode && !isAdmin) {
    return <MaintenanceMode />;
  }

  return <>{children}</>;
};

export default MaintenanceWrapper;
